import { useState, useEffect, useCallback } from 'react';
import { Droplets, Thermometer, Filter, Check, AlertTriangle, Snowflake } from 'lucide-react';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Adding Dodecanol (Lauryl Alcohol)',
    content: 'Dodecanol (C₁₂H₂₅OH) is a 12-carbon primary alcohol. It serves as the hydrophobic tail of the detergent molecule. The long carbon chain provides the nonpolar, water-fearing portion essential for surfactant properties.',
    whatHappens: 'The dodecanol is added to the reaction vessel. It appears as a clear, slightly viscous liquid with a faint waxy odor.',
    observe: [
      'Clear, oily liquid',
      'Slight waxy odor',
      'Does not mix with water',
      'Forms the hydrophobic tail of detergent'
    ]
  },
  2: {
    title: 'Sulfonation with H₂SO₄',
    content: 'CAUTION: Concentrated sulfuric acid is highly corrosive. Wear eye protection! While stirring with a glass rod, slowly add 2.0 mL of concentrated sulfuric acid in the fume hood. Stir for one minute and allow to stand for ten minutes.\n\nC₁₂H₂₅OH + H₂SO₄ → C₁₂H₂₅OSO₃H + H₂O\n\nThis creates the sulfate ester. The reaction is highly exothermic!',
    whatHappens: 'The sulfuric acid reacts with dodecanol. Heat is released (exothermic), and the mixture may darken slightly. Add slowly with stirring.',
    observe: [
      'Temperature rises (exothermic)',
      'Color may darken slightly',
      'Add 2.0 mL acid SLOWLY in fume hood',
      'Stir 1 min, stand 10 min'
    ]
  },
  3: {
    title: 'Preparing Ice-Salt Bath and Na₂CO₃',
    content: 'Ice-salt bath: Fill a 250 mL beaker one-third full of ice; add about 10 g of salt and enough water to bring the total volume to 75 mL.\n\nNa₂CO₃ solution: Place 15.0 mL of dilute (1 M) sodium carbonate in a small beaker. Add 4 drops of phenolphthalein indicator and mix well. (Phenolphthalein is colourless at pH < 8.3, pink at pH 8.3-10.0.)',
    whatHappens: 'The Na₂CO₃/phenolphthalein solution is prepared. The indicator will show pink when the neutralization endpoint is reached.',
    observe: [
      'Prepare ice-salt bath (10 g salt, 75 mL)',
      'Add 4 drops phenolphthalein to 15.0 mL Na₂CO₃',
      'Solution should appear pink initially',
      'Will turn colorless when acid is added'
    ]
  },
  4: {
    title: 'Neutralization with Na₂CO₃',
    content: 'Slowly and carefully pour the sodium carbonate solution into the dodecanol-sulfuric acid mixture. Stir until the pink colour disappears. A large amount of solid detergent should form after effervescence ceases.\n\nC₁₂H₂₅OSO₃H + Na₂CO₃ → C₁₂H₂₅OSO₃Na + H₂O + CO₂↑\n\nThe sodium lauryl sulfate formed is the active detergent ingredient.',
    whatHappens: 'Bubbles of CO₂ gas are released as the acid is neutralized. Stir until the pink colour disappears.',
    observe: [
      'Vigorous CO₂ bubbling',
      'Solution warms slightly',
      'Stir until pink disappears',
      'Solid detergent forms after effervescence'
    ]
  },
  5: {
    title: 'Cooling in Ice-Salt Bath',
    content: 'Pour the mixture into the ice-salt bath (prepared earlier with 10 g salt). Stir vigorously to break up the detergent. The ionic detergent is less soluble in cold water, allowing better separation.',
    whatHappens: 'The solution is placed in the ice-salt bath. White crystalline detergent begins to precipitate as the temperature drops.',
    observe: [
      'Temperature drops in ice-salt bath',
      'White crystals form',
      'Stir vigorously to break up detergent',
      'Allow sufficient cooling time'
    ]
  },
  6: {
    title: 'Filtering and Washing',
    content: 'Filter the precipitated detergent by vacuum filtration and wash the collected detergent in the funnel twice with 10 mL portions of ice-cold water. The washings remove residual acid and salt from the product.',
    whatHappens: 'The crystals are collected on filter paper. The filtrate contains water, excess sodium carbonate, and any remaining impurities.',
    observe: [
      'White crystalline solid collects',
      'Wash twice with 10 mL ice-cold water',
      'Filtrate may be slightly colored',
      'Product may foam when handled'
    ]
  }
};

interface Part2DetergentMakingProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part2DetergentMaking({ onStepComplete, currentStep = 1 }: Part2DetergentMakingProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // State for detergent synthesis
  const [dodecanolAdded, setDodecanolAdded] = useState(false);
  const [sulfuricAcidAdded, setSulfuricAcidAdded] = useState(false);
  const [acidAddProgress, setAcidAddProgress] = useState(0);
  const [indicatorAdded, setIndicatorAdded] = useState(false);
  const [neutralizationProgress, setNeutralizationProgress] = useState(0);
  const [isNeutralized, setIsNeutralized] = useState(false);
  const [cooled, setCooled] = useState(false);
  const [detergentCollected, setDetergentCollected] = useState(false);
  const [detergentMass, setDetergentMass] = useState(0);
  const [temperature, setTemperature] = useState(25);

  // Solution color based on state
  const getSolutionColor = () => {
    if (!dodecanolAdded) return 'transparent';
    if (!sulfuricAcidAdded) return '#FFF8DC'; // Pale yellow (dodecanol)
    if (!indicatorAdded) return '#FFFACD'; // Lemon chiffon (sulfonated)
    if (neutralizationProgress < 100) return '#FFB6C1'; // Light pink (acidic with indicator)
    return '#FFB6C1'; // Pink (neutralized - endpoint)
  };

  // Acid addition simulation
  useEffect(() => {
    if (dodecanolAdded && acidAddProgress > 0 && acidAddProgress < 100) {
      const interval = setInterval(() => {
        setAcidAddProgress(prev => Math.min(prev + 2, 100));
        setTemperature(prev => Math.min(prev + 0.5, 45)); // Exothermic reaction
      }, 100);
      return () => clearInterval(interval);
    }
    if (acidAddProgress >= 100 && !sulfuricAcidAdded) {
      setSulfuricAcidAdded(true);
      if (onStepComplete) onStepComplete('detergentMaking-2');
    }
  }, [dodecanolAdded, acidAddProgress, sulfuricAcidAdded, onStepComplete]);

  // Neutralization simulation
  useEffect(() => {
    if (indicatorAdded && neutralizationProgress > 0 && neutralizationProgress < 100) {
      const interval = setInterval(() => {
        setNeutralizationProgress(prev => Math.min(prev + 1.5, 100));
      }, 100);
      return () => clearInterval(interval);
    }
    if (neutralizationProgress >= 100 && !isNeutralized) {
      setIsNeutralized(true);
      if (onStepComplete) onStepComplete('detergentMaking-4');
    }
  }, [indicatorAdded, neutralizationProgress, isNeutralized, onStepComplete]);

  // Cooling simulation
  useEffect(() => {
    if (cooled) {
      const interval = setInterval(() => {
        setTemperature(prev => Math.max(prev - 2, 5));
      }, 200);
      return () => clearInterval(interval);
    }
  }, [cooled]);

  const handleAddDodecanol = useCallback(() => {
    setDodecanolAdded(true);
    if (onStepComplete) onStepComplete('detergentMaking-1');
  }, [onStepComplete]);

  const handleAddAcid = useCallback(() => {
    if (dodecanolAdded) {
      setAcidAddProgress(1); // Start the acid addition
    }
  }, [dodecanolAdded]);

  const handleAddIndicator = useCallback(() => {
    if (sulfuricAcidAdded) {
      setIndicatorAdded(true);
      if (onStepComplete) onStepComplete('detergentMaking-3');
    }
  }, [sulfuricAcidAdded, onStepComplete]);

  const handleNeutralize = useCallback(() => {
    if (indicatorAdded) {
      setNeutralizationProgress(1); // Start neutralization
    }
  }, [indicatorAdded]);

  const handleCool = useCallback(() => {
    if (isNeutralized) {
      setCooled(true);
      if (onStepComplete) onStepComplete('detergentMaking-5');
    }
  }, [isNeutralized, onStepComplete]);

  const handleCollect = useCallback(() => {
    if (cooled && temperature <= 10) {
      const mass = 3.5 + Math.random() * 0.5;
      setDetergentMass(parseFloat(mass.toFixed(2)));
      setDetergentCollected(true);
      if (onStepComplete) onStepComplete('detergentMaking-6');
    }
  }, [cooled, temperature, onStepComplete]);

  return (
    <LabBench
      experimentName="Detergent Making"
      completedCount={[dodecanolAdded, sulfuricAcidAdded, indicatorAdded, isNeutralized, cooled, detergentCollected].filter(Boolean).length}
      totalSteps={6}
      equipment={['Beaker', 'Ice bath', 'Filter apparatus', 'Stirring rod']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="detergent-making-lab">
        {/* Main Reaction Beaker */}
        <div className="main-vessel">
          <svg viewBox="0 0 140 180" width={140} height={180}>
            <defs>
              <linearGradient id="beaker-glass" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
              </linearGradient>
            </defs>

            {/* Beaker */}
            <path
              d="M25 30 L25 150 Q25 160 35 160 L105 160 Q115 160 115 150 L115 30"
              fill="url(#beaker-glass)"
              stroke="#7A9BB8"
              strokeWidth="2"
            />

            {/* Solution */}
            {dodecanolAdded && (
              <rect
                x="28"
                y="70"
                width="84"
                height="85"
                fill={getSolutionColor()}
                opacity="0.8"
              />
            )}

            {/* Bubbles during acid addition */}
            {acidAddProgress > 0 && acidAddProgress < 100 && (
              <g>
                <circle cx="50" cy="100" r="3" fill="white" opacity="0.4">
                  <animate attributeName="cy" values="100;60;100" dur="0.8s" repeatCount="indefinite" />
                </circle>
                <circle cx="80" cy="110" r="2" fill="white" opacity="0.4">
                  <animate attributeName="cy" values="110;70;110" dur="1s" repeatCount="indefinite" />
                </circle>
              </g>
            )}

            {/* CO₂ bubbles during neutralization */}
            {neutralizationProgress > 0 && neutralizationProgress < 100 && (
              <g>
                <circle cx="45" cy="90" r="4" fill="white" opacity="0.5">
                  <animate attributeName="cy" values="90;40;90" dur="0.6s" repeatCount="indefinite" />
                </circle>
                <circle cx="70" cy="95" r="3" fill="white" opacity="0.5">
                  <animate attributeName="cy" values="95;50;95" dur="0.7s" repeatCount="indefinite" />
                </circle>
                <circle cx="90" cy="85" r="3" fill="white" opacity="0.5">
                  <animate attributeName="cy" values="85;45;85" dur="0.5s" repeatCount="indefinite" />
                </circle>
              </g>
            )}

            {/* Ice bath indication */}
            {cooled && (
              <g>
                <rect x="15" y="165" width="110" height="15" fill="#B0E0E6" opacity="0.7" rx="3" />
                <circle cx="30" cy="172" r="4" fill="white" opacity="0.6" />
                <circle cx="70" cy="170" r="5" fill="white" opacity="0.5" />
                <circle cx="100" cy="173" r="3" fill="white" opacity="0.6" />
              </g>
            )}

            {/* Graduation marks */}
            {[50, 100, 150, 200].map((ml, i) => (
              <g key={ml}>
                <line x1="115" y1={150 - i * 25} x2="125" y2={150 - i * 25} stroke="#5A7A95" strokeWidth="1" />
                <text x="128" y={153 - i * 25} fontSize="8" fill="#5A7A95">{ml}</text>
              </g>
            ))}

            {/* Stirring rod */}
            <rect x="68" y="20" width="4" height="130" fill="#999" rx="2" />
          </svg>

          <div className="vessel-info">
            <div className="temp-badge" style={{ background: temperature > 30 ? 'rgba(255,90,54,0.2)' : 'rgba(0,168,107,0.2)' }}>
              <Thermometer size={14} />
              <span>{temperature.toFixed(0)}°C</span>
            </div>
          </div>
        </div>

        {/* Reagent Bottles Row */}
        <div className="reagents-row">
          {/* Dodecanol */}
          <div className="reagent-container">
            <div
              className={`reagent-bottle ${!dodecanolAdded ? 'clickable' : 'used'}`}
              onClick={!dodecanolAdded ? handleAddDodecanol : undefined}
            >
              <svg viewBox="0 0 50 80" width={50} height={80}>
                <rect x="12" y="25" width="26" height="45" fill="rgba(200,220,240,0.5)" rx="4" />
                <rect x="16" y="35" width="18" height="30" fill="#FFF8DC" opacity="0.8" />
                <rect x="15" y="12" width="20" height="15" fill="#4A4A4A" rx="2" />
              </svg>
              <span className="reagent-name">Dodecanol</span>
            </div>
            <button className="step-info-btn" onClick={() => setShowInfo(1)}>?</button>
          </div>

          {/* Sulfuric Acid */}
          <div className="reagent-container">
            <div
              className={`reagent-bottle ${dodecanolAdded && !sulfuricAcidAdded ? 'clickable' : sulfuricAcidAdded ? 'used' : ''}`}
              onClick={dodecanolAdded && !sulfuricAcidAdded ? handleAddAcid : undefined}
            >
              <svg viewBox="0 0 50 80" width={50} height={80}>
                <rect x="12" y="25" width="26" height="45" fill="rgba(200,220,240,0.5)" rx="4" />
                <rect x="16" y="35" width="18" height="30" fill="#FFFACD" opacity="0.8" />
                <rect x="15" y="12" width="20" height="15" fill="#4A4A4A" rx="2" />
                <rect x="14" y="65" width="22" height="8" fill="#FFD700" rx="1" />
                <text x="25" y="72" fontSize="6" fill="#000" textAnchor="middle" fontWeight="bold">ACID</text>
              </svg>
              <span className="reagent-name">H₂SO₄</span>
              {acidAddProgress > 0 && acidAddProgress < 100 && (
                <div className="mini-progress">
                  <div className="mini-fill" style={{ width: `${acidAddProgress}%` }} />
                </div>
              )}
            </div>
            <button className="step-info-btn" onClick={() => setShowInfo(2)}>?</button>
          </div>

          {/* Phenolphthalein */}
          <div className="reagent-container">
            <div
              className={`reagent-bottle ${sulfuricAcidAdded && !indicatorAdded ? 'clickable' : indicatorAdded ? 'used' : ''}`}
              onClick={sulfuricAcidAdded && !indicatorAdded ? handleAddIndicator : undefined}
            >
              <svg viewBox="0 0 50 80" width={50} height={80}>
                <rect x="17" y="30" width="16" height="35" fill="rgba(200,220,240,0.5)" rx="3" />
                <rect x="20" y="40" width="10" height="20" fill="transparent" stroke="#CCC" strokeWidth="1" />
                <rect x="18" y="18" width="14" height="14" fill="#4A4A4A" rx="2" />
              </svg>
              <span className="reagent-name">Indicator</span>
            </div>
            <button className="step-info-btn" onClick={() => setShowInfo(3)}>?</button>
          </div>

          {/* Sodium Carbonate */}
          <div className="reagent-container">
            <div
              className={`reagent-bottle ${indicatorAdded && !isNeutralized ? 'clickable' : isNeutralized ? 'used' : ''}`}
              onClick={indicatorAdded && !isNeutralized ? handleNeutralize : undefined}
            >
              <svg viewBox="0 0 50 80" width={50} height={80}>
                <rect x="10" y="25" width="30" height="45" fill="rgba(200,220,240,0.5)" rx="4" />
                <rect x="14" y="40" width="22" height="25" fill="#FFFFFF" opacity="0.9" />
                <rect x="13" y="12" width="24" height="15" fill="#4A4A4A" rx="2" />
              </svg>
              <span className="reagent-name">Na₂CO₃</span>
              {neutralizationProgress > 0 && neutralizationProgress < 100 && (
                <div className="mini-progress">
                  <div className="mini-fill neutralize" style={{ width: `${neutralizationProgress}%` }} />
                </div>
              )}
            </div>
            <button className="step-info-btn" onClick={() => setShowInfo(4)}>?</button>
          </div>
        </div>

        {/* Status Messages */}
        <div className="status-area">
          {acidAddProgress > 0 && acidAddProgress < 100 && (
            <div className="status-message warning">
              <AlertTriangle size={14} />
              <span>Adding H₂SO₄ slowly... Exothermic reaction!</span>
            </div>
          )}
          {neutralizationProgress > 0 && neutralizationProgress < 100 && (
            <div className="status-message info">
              <Droplets size={14} />
              <span>Neutralizing... CO₂ evolution observed</span>
            </div>
          )}
          {isNeutralized && !cooled && (
            <div className="status-message success">
              <Check size={14} />
              <span>Endpoint reached (pink color persists)</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          {isNeutralized && !cooled && (
            <div className="action-btn-group">
              <button className="action-btn cool" onClick={handleCool}>
                <Snowflake size={16} />
                Cool in Ice Bath
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(5)}>?</button>
            </div>
          )}
          {cooled && temperature <= 10 && !detergentCollected && (
            <div className="action-btn-group">
              <button className="action-btn success" onClick={handleCollect}>
                <Filter size={16} />
                Filter and Collect
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(6)}>?</button>
            </div>
          )}
        </div>

        {/* Final Product */}
        {detergentCollected && (
          <div className="final-product">
            <div className="product-display">
              <svg viewBox="0 0 80 60" width={80} height={60}>
                <ellipse cx="40" cy="35" rx="30" ry="15" fill="#FFFFFF" stroke="#DDD" strokeWidth="1" />
                <ellipse cx="40" cy="32" rx="25" ry="10" fill="#F8F8FF" opacity="0.5" />
              </svg>
              <div className="product-info">
                <span className="product-mass">{detergentMass}g</span>
                <span className="product-name">Sodium Lauryl Sulfate</span>
                <span className="product-formula">C12H25OSO3Na</span>
              </div>
            </div>
          </div>
        )}

        {/* Chemical Equation */}
        <div className="equation-box">
          <span className="equation-title">Reaction:</span>
          <span className="equation">
            C12H25OH + H₂SO₄ → C12H25OSO3H + H₂O
          </span>
          <span className="equation">
            C12H25OSO3H + Na₂CO₃ → C12H25OSO3Na + H₂O + CO₂
          </span>
        </div>

        <style>{`
          .detergent-making-lab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            padding: 20px;
            width: 100%;
          }

          .main-vessel {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .vessel-info {
            display: flex;
            gap: 12px;
          }

          .temp-badge {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
          }

          .reagents-row {
            display: flex;
            gap: 24px;
            flex-wrap: wrap;
            justify-content: center;
          }

          .reagent-bottle {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            padding: 8px;
            border-radius: 8px;
            transition: all 0.2s;
            opacity: 0.5;
          }

          .reagent-bottle.clickable {
            opacity: 1;
            cursor: pointer;
            background: rgba(255, 90, 54, 0.1);
            border: 1px solid var(--primary);
          }

          .reagent-bottle.clickable:hover {
            transform: translateY(-4px);
            background: rgba(255, 90, 54, 0.2);
          }

          .reagent-bottle.used {
            opacity: 0.6;
          }

          .reagent-name {
            font-size: 10px;
            color: var(--text-secondary);
          }

          .mini-progress {
            width: 40px;
            height: 4px;
            background: var(--bg-dark);
            border-radius: 2px;
            overflow: hidden;
          }

          .mini-fill {
            height: 100%;
            background: #FFA500;
            transition: width 0.1s;
          }

          .mini-fill.neutralize {
            background: #FF69B4;
          }

          .status-area {
            min-height: 30px;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 12px;
          }

          .status-message.warning {
            background: rgba(255, 193, 7, 0.1);
            color: #FFC107;
          }

          .status-message.info {
            background: rgba(0, 168, 107, 0.1);
            color: #00A86B;
          }

          .status-message.success {
            background: rgba(255, 105, 180, 0.1);
            color: #FF69B4;
          }

          .action-buttons {
            display: flex;
            gap: 12px;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn.cool {
            background: #4AA8D8;
            color: white;
          }

          .action-btn.cool:hover {
            background: #3A98C8;
          }

          .action-btn.success {
            background: #00A86B;
            color: white;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .final-product {
            background: var(--bg-elevated);
            padding: 16px 24px;
            border-radius: 12px;
            border: 2px solid #00A86B;
          }

          .product-display {
            display: flex;
            align-items: center;
            gap: 16px;
          }

          .product-info {
            display: flex;
            flex-direction: column;
          }

          .product-mass {
            font-size: 20px;
            font-weight: 600;
            color: var(--text-primary);
          }

          .product-name {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .product-formula {
            font-size: 11px;
            color: var(--text-muted);
            font-family: monospace;
          }

          .equation-box {
            background: var(--bg-elevated);
            padding: 12px 20px;
            border-radius: 8px;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .equation-title {
            font-size: 10px;
            color: var(--text-muted);
            text-transform: uppercase;
          }

          .equation {
            font-size: 12px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
          }

          .reagent-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }

          .action-btn-group {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp5"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
